import { getAllStatesAsync, searchByZipAsync } from '@/lib/geographic-data';
import type { State } from '@/lib/types';
import { isZipLikeInput, normalizeZipCode, normalizeZipCodes } from '@/lib/zip';

type FirestoreService = typeof import('./firestore-service');
let firestoreServicePromise: Promise<FirestoreService> | null = null;

function loadFirestoreService(): Promise<FirestoreService> {
  if (!firestoreServicePromise) firestoreServicePromise = import('./firestore-service');
  return firestoreServicePromise;
}

interface CityIndexEntry {
  name: string;
  county: string;
  state: string;
  slug: string;
  zipCodes: string[];
}

export interface CitySearchResult extends CityIndexEntry {
  score: number;
  bestZip?: string;
  matchText: string;
  confidence?: 'exact' | 'official' | 'zcta-approximate';
  relationship?: 'primary' | 'secondary' | 'approximate';
}

export type ZipSearchDecision =
  | { kind: 'no-match'; zip: string; candidates: [] }
  | { kind: 'single'; zip: string; candidates: [CitySearchResult] }
  | { kind: 'ambiguous'; zip: string; candidates: CitySearchResult[] };

/** Shared decision boundary used by every ZIP search surface. */
export function classifyZipSearchCandidates(candidates: CitySearchResult[], zip: string): ZipSearchDecision {
  const normalizedCandidates = [...candidates];
  if (normalizedCandidates.length === 0) return { kind: 'no-match', zip, candidates: [] };
  if (normalizedCandidates.length === 1) return { kind: 'single', zip, candidates: [normalizedCandidates[0]] };
  return { kind: 'ambiguous', zip, candidates: normalizedCandidates };
}

function indexedZipCandidateToResult(
  candidate: Awaited<ReturnType<FirestoreService['searchLocationsByZip']>>[number],
  zip: string,
): CitySearchResult {
  return {
    name: candidate.cityName,
    county: candidate.countyName,
    state: candidate.stateCode,
    slug: candidate.citySlug,
    zipCodes: [zip],
    score: 0,
    bestZip: zip,
    matchText: `${candidate.cityName}, ${candidate.stateCode}`,
    confidence: candidate.confidence,
    relationship: candidate.relationship,
  };
}

let cityIndex: CityIndexEntry[] | null = null;

function normalizeText(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '');
}

function buildCityIndex(states: State[]): CityIndexEntry[] {
  const entries: CityIndexEntry[] = [];

  states.forEach((state) => {
    state.counties?.forEach((county) => {
      county.cities?.forEach((city) => {
        entries.push({
          name: city.name,
          county: city.county,
          state: city.state,
          slug: city.slug,
          zipCodes: normalizeZipCodes(city.zipCodes),
        });
      });
    });
  });

  return entries;
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  const aLen = a.length;
  const bLen = b.length;
  if (aLen === 0) return bLen;
  if (bLen === 0) return aLen;

  const prev = new Array(bLen + 1).fill(0);
  const curr = new Array(bLen + 1).fill(0);

  for (let j = 0; j <= bLen; j++) {
    prev[j] = j;
  }

  for (let i = 1; i <= aLen; i++) {
    curr[0] = i;
    for (let j = 1; j <= bLen; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= bLen; j++) {
      prev[j] = curr[j];
    }
  }

  return curr[bLen];
}

function scoreCity(query: string, entry: CityIndexEntry): number {
  const normQuery = normalizeText(query);
  const normName = normalizeText(entry.name);
  const normCounty = normalizeText(entry.county);
  const combined = `${normName} ${normCounty}`.trim();

  // Prioritize direct substring matches over distance
  if (normName.startsWith(normQuery)) return 0;
  if (combined.startsWith(normQuery)) return 1;

  const nameDistance = levenshtein(normQuery, normName);
  const countyDistance = levenshtein(normQuery, combined);
  return Math.min(nameDistance, countyDistance);
}

async function ensureCityIndex(): Promise<CityIndexEntry[]> {
  if (cityIndex) return cityIndex;
  const states = await getAllStatesAsync();
  cityIndex = buildCityIndex(states || []);
  return cityIndex;
}

export async function searchCities(query: string, limit = 7): Promise<CitySearchResult[]> {
  if (!query.trim()) return [];
  const index = await ensureCityIndex();
  if (index.length === 0) {
    try {
      const { searchCitiesByName } = await loadFirestoreService();
      return (await searchCitiesByName(query, limit)).map((city) => ({
        name: city.name,
        county: city.county,
        state: city.state,
        slug: city.slug,
        zipCodes: normalizeZipCodes(city.zipCodes),
        score: 0,
        bestZip: normalizeZipCodes(city.zipCodes)[0],
        matchText: `${city.name}, ${city.state}`,
      }));
    } catch (error) {
      console.warn('[Search] City prefix lookup failed; returning no suggestions', error);
      return [];
    }
  }

  const results = index
    .map((entry) => {
      const score = scoreCity(query, entry);
      return {
        ...entry,
        score,
        bestZip: entry.zipCodes[0],
        matchText: `${entry.name}, ${entry.state}`,
      } as CitySearchResult;
    })
    .sort((a, b) => a.score - b.score || a.name.localeCompare(b.name))
    .slice(0, limit);

  return results;
}

export async function searchCityOrZip(query: string) {
  const trimmed = query.trim();
  const zip = isZipLikeInput(trimmed) ? normalizeZipCode(trimmed) : null;

  if (zip) {
    const { searchLocationsByZip } = await loadFirestoreService();
    const indexedCandidates = await searchLocationsByZip(zip);
    if (indexedCandidates.length > 0) {
      const candidates = indexedCandidates.map((candidate) => indexedZipCandidateToResult(candidate, zip));
      const decision = classifyZipSearchCandidates(candidates, zip);
      return { kind: 'zip' as const, city: candidates[0], candidates: decision.candidates, zip };
    }

    const city = await searchByZipAsync(zip);
    if (city) {
      const fallback: CitySearchResult = {
        name: city.name,
        county: city.county,
        state: city.state,
        slug: city.slug,
        zipCodes: city.zipCodes,
        score: 0,
        bestZip: zip,
        matchText: `${city.name}, ${city.state}`,
      };
      return {
        kind: 'zip' as const,
        city: fallback,
        candidates: [fallback],
        zip,
      };
    }

    // ZIP-like input must fail closed. Never reinterpret an unresolved ZIP as
    // a fuzzy city query and route the user to an unrelated place.
    return null;
  }

  const cities = await searchCities(trimmed, 1);
  if (cities.length > 0) {
    return {
      kind: 'city' as const,
      city: cities[0],
      candidates: cities,
      zip: cities[0].bestZip,
    };
  }

  return null;
}
